import React from 'react';
import {
  BaseActionSheet,
  Props as BaseActionSheetProps,
} from './base-action-sheet';
import { Button } from './button';
import {
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { TYPOGRAPHY } from '@/styles';

export interface ContentProps {
  actions: string[];
  buttonContainerStyle?: StyleProp<ViewStyle>;
  dangerIndices?: number[];
  disabledIndices?: number[];
  footer?: React.ReactNode;
  horizontalButtons?: boolean;
  imageComponent?: React.ReactNode;
  loadingActionIndex?: number | null;
  message?: string | React.ReactNode;
  onAction: (action: string, index: number) => void;
  primaryIndices?: number[];
  title?: string;
  titleStyle?: StyleProp<TextStyle>;
}

export type Props = BaseActionSheetProps & ContentProps;

export const ActionSheetContent: React.FC<ContentProps> = ({
  actions,
  buttonContainerStyle,
  dangerIndices = [],
  disabledIndices = [],
  footer,
  horizontalButtons = false,
  imageComponent,
  loadingActionIndex,
  message,
  onAction,
  primaryIndices = [0],
  title,
  titleStyle,
}) => {
  let messageComponent = null;

  if (message) {
    if (typeof message === 'string') {
      messageComponent = (
        <Text style={[TYPOGRAPHY.actionSheetMessageText, styles.messageText]}>
          {message}
        </Text>
      );
    } else {
      messageComponent = message;
    }
  }

  return (
    <>
      {imageComponent ? (
        <View style={styles.imageContainer}>{imageComponent}</View>
      ) : null}
      {title ? (
        <Text
          style={[
            TYPOGRAPHY.h2,
            styles.titleText,
            message ? styles.titleWithMessageText : null,
            titleStyle,
          ]}
        >
          {title}
        </Text>
      ) : null}
      {messageComponent}
      <View
        style={[
          horizontalButtons ? styles.actionsContainerHorizontal : null,
          buttonContainerStyle,
        ]}
      >
        {actions.map((action, index) => (
          <View
            key={action}
            style={[
              styles.buttonContainer,
              horizontalButtons ? styles.buttonContainerHorizontal : null,
              index === actions.length - 1 ? styles.buttonContainerLast : null,
            ]}
          >
            <Button
              disabled={disabledIndices.includes(index)}
              loading={index === loadingActionIndex}
              onPress={() => onAction(action, index)}
              // Let the parent component define tracking for actions
              testID={`action-sheet-button-${index}`}
              trackingData={null}
              type={
                primaryIndices.includes(index)
                  ? 'primary'
                  : dangerIndices.includes(index)
                    ? 'danger'
                    : 'tertiary'
              }
            >
              {action}
            </Button>
          </View>
        ))}
      </View>
      {footer ? <View style={styles.footer}>{footer}</View> : null}
    </>
  );
};

export const ActionSheet: React.FC<Props> = ({
  actions,
  buttonContainerStyle,
  dangerIndices = [],
  disabledIndices = [],
  footer,
  horizontalButtons = false,
  imageComponent,
  isVisible,
  loadingActionIndex,
  message,
  onAction,
  onClose,
  primaryIndices = [0],
  title,
  titleStyle,
  ...rest
}) => {
  return (
    <BaseActionSheet isVisible={isVisible} onClose={onClose} {...rest}>
      <ActionSheetContent
        actions={actions}
        buttonContainerStyle={buttonContainerStyle}
        dangerIndices={dangerIndices}
        disabledIndices={disabledIndices}
        footer={footer}
        horizontalButtons={horizontalButtons}
        imageComponent={imageComponent}
        loadingActionIndex={loadingActionIndex}
        message={message}
        onAction={onAction}
        primaryIndices={primaryIndices}
        title={title}
        titleStyle={titleStyle}
      />
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  actionsContainerHorizontal: {
    flexDirection: 'row-reverse',
  },
  buttonContainer: {
    marginBottom: 16,
  },
  buttonContainerHorizontal: {
    flex: 1,
    marginBottom: 0,
    marginLeft: 16,
  },
  buttonContainerLast: {
    marginBottom: 0,
    marginLeft: 0,
  },
  footer: {
    alignItems: 'center',
    marginTop: 12,
  },
  imageContainer: {
    alignItems: 'center',
    marginBottom: 40,
    marginTop: 24,
  },
  messageText: {
    marginBottom: 24,
  },
  titleText: {
    marginBottom: 24,
    textAlign: 'center',
  },
  titleWithMessageText: {
    marginBottom: 16,
  },
});
